import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../services'
import { queryKeys } from '../lib/queryClient'

/**
 * API response structure for getHysteresisSettings
 *
 * @remarks
 * The API returns hysteresis settings in a flat structure with the hysteresis value directly,
 * not nested under a 'data' property.
 *
 * Example response: { hysteresis: 5, unit: 'degrees' }
 */
export interface GetHysteresisSettingsResponse {
  hysteresis: number
  unit?: string
  [key: string]: any
}

/**
 * React Query hook for fetching hysteresis settings
 *
 * Cache Strategy:
 * - staleTime: 1 hour (rarely changes)
 * - gcTime: 2 hours (keep in memory for very extended period)
 * - Service Worker: CacheFirst with 2h expiration
 *
 * Hysteresis settings are global configuration that rarely change,
 * so we cache them aggressively to minimize API calls.
 *
 * @param enabled - Whether the query should run (default: true)
 */
export function useHysteresisSettings(enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.hysteresisSettings,
    queryFn: async () => {
      const result = await api.getHysteresisSettings()
      return result as GetHysteresisSettingsResponse
    },
    staleTime: 60 * 60 * 1000, // 1 hour - rarely changes
    gcTime: 2 * 60 * 60 * 1000, // 2 hours
    enabled,
    retry: 2,
    retryDelay: 1000,
  })
}

/**
 * Mutation hook for updating hysteresis settings
 *
 * Uses optimistic updates for instant UI feedback
 * Automatically invalidates and refetches on success
 */
export function useUpdateHysteresisSettings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (hysteresis: number) => {
      return await api.updateHysteresisSettings(hysteresis)
    },
    onMutate: async (newHysteresis) => {
      const queryKey = queryKeys.hysteresisSettings

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey })

      // Snapshot previous value
      const previousSettings = queryClient.getQueryData(queryKey)

      // Optimistically update cache
      queryClient.setQueryData(queryKey, (old: GetHysteresisSettingsResponse | undefined) => {
        if (!old) return old

        return {
          ...old,
          hysteresis: newHysteresis,
        }
      })

      // Return context for rollback on error
      return { previousSettings, queryKey }
    },
    onError: (err, newHysteresis, context) => {
      // Rollback on error
      if (context?.previousSettings) {
        queryClient.setQueryData(context.queryKey, context.previousSettings)
      }
    },
    onSuccess: () => {
      // Refetch to ensure sync with server
      queryClient.invalidateQueries({
        queryKey: queryKeys.hysteresisSettings,
      })
    },
  })
}
